import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import useRouteParams from "../../hooks/useRouteParams";
import { FileUploader } from "baseui/file-uploader";
import { Block } from "baseui/block";
import {
  uploadTemplateObjFile,
  cancelUploadTemplateObjFile,
} from "../../redux/template/TemplateThunk";
import EmptyListNotificationComponent from "../../components/empty-list-notification/EmptyListNotificationComponent";
import SuccessNotificationComponent from "../../components/success-notification/SuccessNotificationComponent";
import ObjViewer from "../../components/obj-viewer/ObjViewer";
import { getSignedUrl } from "../../helpers/S3Helpers";
import { GET_SIGNED_URL_PATH } from "../../constants/ApiConstants";
import { useTranslation } from "react-i18next";
import { ProgressBar, SIZE as PSIZE } from "baseui/progress-bar";

function TemplateObjectFileContentView(props) {
  const { t } = useTranslation("template");
  const { searchParams } = useRouteParams();
  const [status, setStatus] = useState("idle");
  const [hasFile, setHasFile] = useState(false);
  const [objUrl, setObjUrl] = useState(null);
  const [viewerKey, setViewerKey] = useState(Date.now());
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const objId = searchParams["objId"];

  const uploadProgress =
    objId && props.uploadProgress[objId] ? props.uploadProgress[objId] : 0;
  const [uploadJustCompleted, setUploadJustCompleted] = useState(false);

  useEffect(() => {
    const fetchUrl = async () => {
      if (!hasFile && searchParams?.objId && props.activeTemplate?.id) {
        try {
          const url = await getSignedUrl(
            GET_SIGNED_URL_PATH,
            "template",
            `${props.activeTemplate.id}/${searchParams?.objId}`
          );
          setHasFile(url !== null);
          setObjUrl(url);
          setFetchingUrl(false);
        } catch (error) {
          setHasFile(false);
          setObjUrl(null);
          setFetchingUrl(false);
        }
      }
    };
    setFetchingUrl(true);
    fetchUrl();
  }, [searchParams?.objId, props.activeTemplate?.id, hasFile]);

  useEffect(() => {
    const checkUploadCompletion = () => {
      const objId = searchParams["objId"];
      const uploadProgress =
        objId && props.uploadProgress[objId] ? props.uploadProgress[objId] : 0;
      if (
        uploadProgress === 100 &&
        status !== "complete" &&
        status !== "idle" &&
        !uploadJustCompleted
      ) {
        setStatus("complete");
        setHasFile(true);
        setUploadJustCompleted(true); // Mark that upload has just completed
      }
    };
    checkUploadCompletion();
  }, [uploadProgress === 100, status]);

  useEffect(() => {
    if (uploadJustCompleted) {
      const timer = setTimeout(() => {
        setStatus("idle");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [uploadJustCompleted]);
  // UseEffect to fetch the new URL once upload is confirmed completed
  useEffect(() => {
    if (uploadJustCompleted) {
      // Asynchronously fetch the new signed URL for the uploaded OBJ file
      const fetchNewSignedUrl = async () => {
        if (props.activeTemplate?.id && objId) {
          try {
            const newUrl = await getSignedUrl(
              GET_SIGNED_URL_PATH,
              "template",
              `${props.activeTemplate.id}/${objId}`
            );
            setObjUrl(newUrl); // Update the OBJ URL with the new signed URL
            setHasFile(true);
            setStatus("complete"); // Set status to complete to trigger success notification
            setViewerKey(Date.now()); // Force re-render of OBJ viewer
          } catch (error) {
            console.error("Error fetching new signed URL", error);
            setHasFile(false);
            setObjUrl(null); // Ensure URL is cleared on error
          } finally {
            // Reset the flag after fetching the URL
            setUploadJustCompleted(false);
            // Optionally reset the status to "idle" after a delay if needed
            // setTimeout(() => setStatus("idle"), 5000);
          }
        }
      };

      fetchNewSignedUrl();
    }
  }, [uploadJustCompleted, props.activeTemplate?.id, objId]);
  const handleFileUpload = (acceptedFiles, rejectedFiles) => {
    if (searchParams["objId"] && acceptedFiles && acceptedFiles.length > 0) {
      setStatus("uploading");
      const fileToUpload = acceptedFiles[0];
      props.onUploadObjectFile(searchParams["objId"], fileToUpload);
    }
  };

  const handleCancelUpload = () => {
    const objId = searchParams["objId"];
    if (objId) {
      props.cancelUpload(objId);
      setStatus("idle");
    }
  };

  return (
    <Block>
      <FileUploader
        onCancel={handleCancelUpload}
        onDrop={handleFileUpload}
        progressAmount={status === "uploading" ? uploadProgress : 0}
        progressMessage={
          status === "uploading"
            ? t("objUploadingProgress", { progress: uploadProgress })
            : ""
        }
      />
      {status === "complete" && (
        <SuccessNotificationComponent message={t("objUploadSuccess")} />
      )}
      <EmptyListNotificationComponent message={t("objOneFileWarning")} />
      {hasFile && (
        <ObjViewer
          key={viewerKey}
          url={objUrl}
          objName={props?.activeTemplate?.selectedFolder?.name}
        />
      )}
      {fetchingUrl && <ProgressBar size={PSIZE.large} infinite />}
      {!fetchingUrl && !hasFile && (
        <EmptyListNotificationComponent message={t("objNoFileUploaded")} />
      )}
    </Block>
  );
}

const mapStateToProps = (state) => ({
  activeTemplate: state.template.activeTemplate,
  uploadProgress: state.template.uploadProgress || {},
});

const mapDispatchToProps = (dispatch) => ({
  onUploadObjectFile: (filename, objFile) =>
    dispatch(uploadTemplateObjFile(filename, objFile)),
  cancelUpload: (filename) => dispatch(cancelUploadTemplateObjFile(filename)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TemplateObjectFileContentView);
